'use client'

import { AbstractModel } from '@/scene/AbstractModel'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export type GlbModelsMap = Readonly<Record<string, AbstractModel>>

type GlbModelsContextValue = {
  isLoaded: boolean
  loadError: Error | null
  models: GlbModelsMap
}

const GlbModelsContext = createContext<GlbModelsContextValue | null>(null)

export function GlbModelsProvider({
  children,
  models: modelsList,
}: {
  children: ReactNode
  models: AbstractModel[]
}) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [loadError, setLoadError] = useState<Error | null>(null)
  const [models, setModels] = useState<GlbModelsMap>({})

  const modelsListRef = useRef(modelsList)
  modelsListRef.current = modelsList
  const listKey = useMemo(
    () => modelsList.map((model) => model.key).sort().join('|'),
    [modelsList],
  )

  useEffect(() => {
    const currentModelsList = modelsListRef.current
    // При смене игры сразу очищаем старую map, чтобы AR-flow не стартовал
    // сцену на ключах от предыдущего marker/game.
    setModels({})
    setIsLoaded(false)

    if (currentModelsList.length === 0) {
      setModels({})
      setLoadError(null)
      setIsLoaded(true)
      return
    }

    let cancelled = false

    void Promise.all(
      currentModelsList.map(
        (model) =>
          new Promise<[string, AbstractModel]>((resolve, reject) => {
            model.load(() => resolve([model.key, model]), reject)
          }),
      ),
    )
      .then((entries) => {
        if (cancelled) return
        setModels(Object.fromEntries(entries))
        setLoadError(null)
        setIsLoaded(true)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setLoadError(e instanceof Error ? e : new Error(String(e)))
        setModels({})
        setIsLoaded(true)
      })

    return () => {
      cancelled = true
    }
  }, [listKey])

  const value = useMemo(
    () => ({ isLoaded, loadError, models }),
    [isLoaded, loadError, models],
  )

  return (
    <GlbModelsContext.Provider value={value}>{children}</GlbModelsContext.Provider>
  )
}

export function useGlbModels(): GlbModelsContextValue {
  const ctx = useContext(GlbModelsContext)
  if (!ctx) {
    throw new Error('useGlbModels must be used within GlbModelsProvider')
  }
  return ctx
}
